import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DisclaimerV1 from "@/components/DisclaimerV1";
import apiClient from "@/lib/apiClient";
import {
  UserPlus, ChevronRight, Search, User,
  Plus, X, Loader2
} from "lucide-react";

function sexLabel(sex) {
  if (!sex) return null;
  if (sex === "M" || sex === "male" || sex === "homme") return "H";
  if (sex === "F" || sex === "female" || sex === "femme") return "F";
  return sex.charAt(0).toUpperCase();
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function PatientRow({ patient, onClick }) {
  const initials = [patient.first_name, patient.last_name]
    .filter(Boolean)
    .map((s) => s[0].toUpperCase())
    .join("") || "?";

  const label =
    [patient.first_name, patient.last_name].filter(Boolean).join(" ") ||
    "Patient sans nom";

  const meta = [
    patient.age ? `${patient.age} ans` : null,
    sexLabel(patient.sex),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left group border-b border-slate-100 last:border-0"
    >
      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{label}</p>
        {meta && <p className="text-xs text-slate-400 mt-0.5">{meta}</p>}
      </div>
      {patient.notes && (
        <p className="text-xs text-slate-400 max-w-[18rem] truncate hidden md:block">
          {patient.notes}
        </p>
      )}
      {patient.created_at && (
        <p className="text-xs text-slate-300 flex-shrink-0 hidden lg:block">
          {formatDate(patient.created_at)}
        </p>
      )}
      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-blue-400 flex-shrink-0 transition-colors" />
    </button>
  );
}

function NewPatientForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    first_name: "", last_name: "", age: "", sex: "", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value })),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name && !form.last_name) {
      setError("Renseignez au moins un prénom ou un nom.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age, 10) : undefined,
      };
      const res = await apiClient.post("/patients/", payload);
      onCreated(res.data);
    } catch {
      setError("Erreur lors de la création. Réessayez.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 bg-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none transition";

  return (
    <form
      onSubmit={handleSubmit}
      className="border border-blue-200 bg-blue-50/40 rounded-xl p-5 mb-4 space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">Nouveau patient</h3>
        <button
          type="button"
          onClick={onCancel}
          className="text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Annuler"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Prénom</label>
          <input className={inputCls} placeholder="Marie" {...field("first_name")} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Nom</label>
          <input className={inputCls} placeholder="Dupont" {...field("last_name")} />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Âge</label>
          <input
            type="number" min="0" max="120"
            className={inputCls} placeholder="82"
            {...field("age")}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Sexe</label>
          <select className={inputCls} {...field("sex")}>
            <option value="">—</option>
            <option value="F">Femme</option>
            <option value="M">Homme</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optionnel)</label>
        <input
          className={inputCls}
          placeholder="Service, contexte clinique..."
          {...field("notes")}
        />
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60"
        >
          {saving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="h-3.5 w-3.5" />
          )}
          Créer
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/patients/");
      setPatients(res.data || []);
    } catch (err) {
      console.error("Erreur chargement patients:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleCreated = (newPatient) => {
    setShowForm(false);
    navigate(`/patient/${newPatient.id}/history`);
  };

  const filtered = patients.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p.first_name?.toLowerCase().includes(q) ||
      p.last_name?.toLowerCase().includes(q) ||
      p.notes?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Mes patients</h1>
          <p className="text-xs text-slate-400 mt-0.5">
            {loading
              ? "Chargement..."
              : patients.length === 0
              ? "Aucun patient"
              : `${patients.length} patient${patients.length > 1 ? "s" : ""}`}
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
        >
          <UserPlus className="h-4 w-4" />
          Nouveau patient
        </button>
      </div>

      {/* Formulaire inline */}
      {showForm && (
        <NewPatientForm
          onCreated={handleCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {/* Recherche (visible si > 3 patients) */}
      {patients.length > 3 && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
            placeholder="Rechercher un patient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Liste / états */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-sm">Chargement...</span>
          </div>
        ) : patients.length === 0 && !showForm ? (
          /* Empty state */
          <div className="text-center py-16 px-6">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
              <User className="h-6 w-6 text-blue-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-1">
              Aucun patient pour l'instant
            </p>
            <p className="text-xs text-slate-400 leading-relaxed max-w-xs mx-auto mb-4">
              Créez votre premier patient pour commencer une révision médicamenteuse.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Créer un patient
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 px-6">
            <Search className="h-7 w-7 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">
              Aucun résultat pour "{search}"
            </p>
          </div>
        ) : (
          filtered.map((p) => (
            <PatientRow
              key={p.id}
              patient={p}
              onClick={() => navigate(`/patient/${p.id}/history`)}
            />
          ))
        )}
      </div>

      {patients.length > 0 && !loading && (
        <p className="text-center text-xs text-slate-400 mt-4">
          Cliquez sur un patient pour consulter ses analyses et documents.
        </p>
      )}

      <DisclaimerV1 />
    </div>
  );
}
